import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import SearchAndFilter from '../components/SearchAndFilter';
import { LoadingSkeleton } from '../components/LoadingSkeleton';

interface Application {
  id: string;
  studentName: string;
  email: string;
  status: 'pending' | 'approved' | 'rejected';
  studentId: string;
  propertyChoice?: string;
  documentsSubmitted?: Record<string, boolean>;
}

const ApplicationsView = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [applications, setApplications] = useState<Application[]>([]);
  const [filteredApplications, setFilteredApplications] = useState<Application[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const { data, error } = await supabase
          .from('students')
          .select(`
            id,
            first_name,
            last_name,
            email,
            student_id,
            application_status,
            property_preferences (property_choice),
            documents (document_type, verified)
          `);

        if (error) throw error;

        const transformedData = data.map(student => ({
          id: student.id,
          studentName: `${student.first_name} ${student.last_name}`,
          email: student.email,
          status: student.application_status,
          studentId: student.student_id,
          propertyChoice: student.property_preferences?.[0]?.property_choice,
          documentsSubmitted: student.documents?.reduce((acc: Record<string, boolean>, doc: any) => {
            acc[doc.document_type] = doc.verified;
            return acc;
          }, {})
        }));

        setApplications(transformedData);
        setFilteredApplications(transformedData);
      } catch (error) {
        console.error('Error fetching applications:', error);
        setError('Failed to load applications');
      } finally {
        setIsLoading(false);
      }
    };

    fetchApplications();
  }, []);

  if (isLoading) return <LoadingSkeleton />;

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Applications</h1>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="mb-6">
          <SearchAndFilter
            items={applications}
            searchKeys={['studentName', 'email', 'studentId']}
            filterOptions={[
              {
                field: 'status',
                label: 'Status',
                options: [
                  { value: 'pending', label: 'Pending' },
                  { value: 'approved', label: 'Approved' },
                  { value: 'rejected', label: 'Rejected' },
                ],
              },
              {
                field: 'propertyChoice',
                label: 'Property',
                options: [
                  { value: 'saratoga', label: 'Saratoga' },
                  { value: 'highlands', label: 'Highlands' },
                ],
              },
            ]}
            onResultsChange={setFilteredApplications}
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Property
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Documents
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredApplications.map((application) => (
                <tr key={application.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {application.studentName}
                      </div>
                      <div className="text-sm text-gray-500">{application.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      application.status === 'approved'
                        ? 'bg-green-100 text-green-800'
                        : application.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {application.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {application.propertyChoice || 'Not selected'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-1">
                      {application.documentsSubmitted &&
                        Object.entries(application.documentsSubmitted).map(([key, verified]) => (
                          <span
                            key={key}
                            className={`w-2 h-2 rounded-full ${
                              verified ? 'bg-green-500' : 'bg-red-500'
                            }`}
                            title={key}
                          />
                        ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ApplicationsView;